// <select> ligado a um vocabulário editável (listas.ts): as opções atuais + um
// botão que abre um modal para incluir, editar e excluir valores (com
// Cancelar/Salvar). Editar troca o nome também nos registros que já o usam.

import { valorDoSistema, type NomeLista } from "../../domain/listas.ts";
import { emTitulo } from "../../lib/em-titulo.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";

export interface ListaSelect {
  element: HTMLElement;
  value(): string;
}

/** Como cada lista se chama no plural (título do modal) e o que se digita
 * nela (placeholder) — escritos à mão, na regra 3, porque o plural e o
 * gênero não se derivam colando "s"/"Novo" num rótulo. */
const NOME: Record<NomeLista, { plural: string; novo: string }> = {
  funcoesFuncionario: { plural: "funções", novo: "Nova função" },
  categoriasInsumo: { plural: "categorias de insumo", novo: "Nova categoria de insumo" },
  unidadesInsumo: { plural: "unidades de medida", novo: "Nova unidade de medida" },
  tiposCliente: { plural: "tipos de cliente", novo: "Novo tipo de cliente" },
  processosPeca: { plural: "processos", novo: "Novo processo" },
  motivosRetencao: { plural: "motivos de retenção", novo: "Novo motivo de retenção" },
  motivosDivergencia: { plural: "motivos de divergência", novo: "Novo motivo de divergência" },
};

export function listaSelect(
  store: Store,
  lista: NomeLista,
  valorAtual: string,
): ListaSelect {
  const { plural, novo: placeholderNovo } = NOME[lista];
  const select = el("select", {}) as HTMLSelectElement;
  const preencher = (): void => {
    const escolhido = select.value || valorAtual;
    select.textContent = "";
    select.append(el("option", { value: "", "data-vazio": "" }, "—"));
    for (const v of [...store.get().listas[lista]].sort((a, b) =>
      a.localeCompare(b, "pt-BR"),
    )) {
      select.append(el("option", { value: v }, v));
    }
    select.value = store.get().listas[lista].includes(escolhido) ? escolhido : "";
  };
  preencher();

  const gerir = botao(
    "secundario",
    { title: `Gerenciar ${plural}` },
    "Gerenciar",
  );
  gerir.addEventListener("click", () => {
    openModal(emTitulo(`Gerenciar ${plural}`), (fechar) => {
      // cópia de trabalho — só grava no "Salvar". `original` é o nome
      // gravado (null = incluído agora): é assim que o Salvar sabe o que foi
      // renomeado e troca o nome também nos registros que já o usam.
      let trabalho: { original: string | null; atual: string }[] = store
        .get()
        .listas[lista].map((v) => ({ original: v, atual: v }));
      let editando: string | null = null; // `atual` do item em edição
      const erro = el("p", { class: "form-erro", role: "alert" });
      const listaEl = el("ul", { class: "lista-editavel" });
      const duplicado = (v: string, ignorar?: string): boolean =>
        trabalho.some((x) => x.atual !== ignorar && x.atual.toLowerCase() === v.toLowerCase());

      const redesenhar = (): void => {
        clear(listaEl);
        if (trabalho.length === 0) {
          listaEl.append(el("li", { class: "muted" }, "Nenhum item."));
        }
        const ordenado = [...trabalho].sort((a, b) => a.atual.localeCompare(b.atual, "pt-BR"));
        for (const item of ordenado) {
          if (editando === item.atual) {
            const inp = el("input", { type: "text", "aria-label": "Novo nome" }) as HTMLInputElement;
            inp.value = item.atual;
            const ok = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Salvar");
            const cancelarEd = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Cancelar");
            const confirmar = (): void => {
              const v = inp.value.trim();
              if (v === "") {
                erro.textContent = "Informe o nome.";
                return;
              }
              if (duplicado(v, item.atual)) {
                erro.textContent = `"${v}" já está na lista.`;
                return;
              }
              item.atual = v;
              editando = null;
              erro.textContent = "";
              redesenhar();
            };
            ok.addEventListener("click", confirmar);
            cancelarEd.addEventListener("click", () => {
              editando = null;
              erro.textContent = "";
              redesenhar();
            });
            inp.addEventListener("keydown", (e) => {
              // Enter confirma só a linha, não o formulário inteiro
              if (e.key === "Enter") {
                e.preventDefault();
                confirmar();
              } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                cancelarEd.click();
              }
            });
            listaEl.append(el("li", {}, el("div", { class: "hist-edit" }, inp, ok, cancelarEd)));
            requestAnimationFrame(() => inp.focus());
            continue;
          }
          // valor do sistema (ADR-0033): fica, sem Editar nem Excluir
          if (item.original != null && valorDoSistema(lista, item.original)) {
            listaEl.append(
              el("li", {}, el("span", {}, item.atual), el("span", { class: "hist-acao muted" }, "Do sistema")),
            );
            continue;
          }
          const editar = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Editar");
          editar.addEventListener("click", () => {
            editando = item.atual;
            erro.textContent = "";
            redesenhar();
          });
          const rm = botao({ variante: "perigo", tamanho: "compacto" }, {}, "Excluir");
          rm.addEventListener("click", () => {
            trabalho = trabalho.filter((x) => x !== item);
            redesenhar();
          });
          listaEl.append(
            el("li", {}, el("span", {}, item.atual), el("span", { class: "hist-acao" }, editar, rm)),
          );
        }
      };
      redesenhar();

      const novo = el("input", {
        type: "text",
        placeholder: placeholderNovo,
      }) as HTMLInputElement;
      const incluir = botao("secundario", {}, "Incluir");
      const addUm = (): void => {
        const v = novo.value.trim();
        if (v === "") return;
        if (duplicado(v)) {
          erro.textContent = `"${v}" já está na lista.`;
          return;
        }
        trabalho.push({ original: null, atual: v });
        novo.value = "";
        erro.textContent = "";
        redesenhar();
        novo.focus();
      };
      incluir.addEventListener("click", addUm);
      novo.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          addUm();
        }
      });

      const cancelar = botao("secundario", {}, "Cancelar");
      cancelar.addEventListener("click", fechar);
      const salvar = botao("primario", { type: "submit" }, "Salvar");

      const form = el(
        "form",
        { class: "form-modal" },
        listaEl,
        el("div", { class: "lista-add" }, novo, incluir),
        erro,
        el("div", { class: "modal-actions" }, cancelar, salvar),
      );
      form.addEventListener("submit", (ev) => {
        ev.preventDefault();
        erro.textContent = "";
        criarAcaoComando(store, erro)(
          // cópia: se a pessoa mexer na lista enquanto o save está na fila,
          // o comando já enfileirado não muda embaixo dos panos
          {
            agregado: "lista",
            cmd: {
              tipo: "definir",
              lista,
              valores: trabalho.map((x) => x.atual),
              renomeados: trabalho
                .filter((x) => x.original != null && x.original !== x.atual)
                .map((x) => ({ de: x.original!, para: x.atual })),
            },
          },
          undefined,
          () => {
            preencher();
            fechar();
          },
        );
      });
      return form;
    });
  });

  return {
    element: el("div", { class: "lista-select" }, select, gerir),
    value: () => select.value,
  };
}
